import { useCallback } from 'react';
import { toast } from 'sonner';
import useLocalStorage from './useLocalStorage';
import { usePolyAuth } from './PolyAuthContext';

/**
 * Shared hook for Fast Open Meeting flow.
 *
 * Uses cached identity JWT (originalUserBundle) + platform session to skip
 * full re-verification for hosts. Falls back to inline QR verification when
 * no cached identity exists or when proofs are insufficient.
 *
 * @param {Object} options
 * @param {Function} options.onLaunchMeeting - Called with (joinUrl, link) on success
 * @param {Function} options.onFallbackVerify - Called with (link) when inline verify fails or is cancelled
 * @returns {{ openMeeting: Function, hasVerification: boolean }}
 */
export default function useFastOpenMeeting({ onLaunchMeeting, onFallbackVerify }) {
    const [originalUserBundle] = useLocalStorage('originalUserBundle');
    const { api_client, login } = usePolyAuth();

    const hasVerification = !!originalUserBundle;

    const startMeeting = useCallback(async (link, jwtBlob) => {
        const response = await api_client.post(`/v2/start/${link.link_uuid}`, {
            jwtBlob: jwtBlob,
        });
        if (!response.data?.join_url) {
            throw new Error('No join_url returned');
        }
        return response.data.join_url;
    }, [api_client]);

    const openMeeting = useCallback(async (link) => {
        try {
            toast.info('Starting meeting...');

            if (!originalUserBundle) {
                // No cached identity — verify inline with meeting-specific proofs
                try {
                    const { raw_jwt } = await login({
                        requiredProofs: link.requiredProofs || [],
                        linkUuid: link.link_uuid,
                    });
                    const joinUrl = await startMeeting(link, raw_jwt);
                    onLaunchMeeting(joinUrl, link);
                    toast.success('Meeting started!');
                } catch (verifyErr) {
                    console.error('Inline verification failed:', verifyErr);
                    onFallbackVerify(link);
                }
                return;
            }

            // Has cached identity — try starting directly
            try {
                const joinUrl = await startMeeting(link, originalUserBundle);
                onLaunchMeeting(joinUrl, link);
                toast.success('Meeting started!');
            } catch (err) {
                if (err.response?.status === 403) {
                    // Missing proofs — upgrade via inline verification
                    toast.info('Additional verification required...');
                    try {
                        const { raw_jwt } = await login({
                            requiredProofs: link.requiredProofs || [],
                            linkUuid: link.link_uuid,
                        });
                        const joinUrl = await startMeeting(link, raw_jwt);
                        onLaunchMeeting(joinUrl, link);
                        toast.success('Meeting started!');
                    } catch (upgradeErr) {
                        console.error('Proof upgrade failed:', upgradeErr);
                        onFallbackVerify(link);
                    }
                } else {
                    console.error('Error starting meeting:', err);
                    onFallbackVerify(link);
                }
            }
        } catch (err) {
            console.error('Error in openMeeting:', err);
            toast.error(err.message || 'Failed to start meeting');
            onFallbackVerify(link);
        }
    }, [originalUserBundle, startMeeting, login, onLaunchMeeting, onFallbackVerify]);

    return { openMeeting, hasVerification };
}
